const sanitizeSlashes = (value: string): string => value.replace(/\\/g, '/');

const isLikelyFileSegment = (segment: string): boolean => {
  return /[A-Za-z0-9_-]+\.[A-Za-z0-9]{2,5}$/.test(segment);
};

const ensureDirectoryPath = (pathname: string): string => {
  const normalizedPath: string = pathname || '/';

  if (normalizedPath.endsWith('/')) {
    return normalizedPath;
  }

  const lastSlashIndex: number = normalizedPath.lastIndexOf('/');
  const lastSegment: string = normalizedPath.slice(lastSlashIndex + 1);
  const looksLikeFile: boolean = isLikelyFileSegment(lastSegment);

  if (looksLikeFile) {
    const directory: string = normalizedPath.slice(0, Math.max(lastSlashIndex, 0));
    return directory.endsWith('/') ? directory || '/' : `${directory}/`;
  }

  return `${normalizedPath}/`;
};

const isLikelyValidPath = (value: string): boolean => /^\/[A-Za-z0-9/._-]*$/.test(value);

const joinOriginAndPath = (origin: string, path: string): string => {
  const normalizedOrigin: string = origin.replace(/\/+$/, '') || '/';
  const normalizedPath: string = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedOrigin}${normalizedPath}`;
};

export const computePublicPath = (candidate: string, fallbackOrigin: string): string => {
  const sanitizedCandidate: string = sanitizeSlashes(candidate);

  try {
    const resolved = new URL(sanitizedCandidate, fallbackOrigin);
    const sanitizedPathname: string = sanitizeSlashes(resolved.pathname);
    if (!isLikelyValidPath(sanitizedPathname)) {
      return '/';
    }
    const sanitizedPath: string = ensureDirectoryPath(sanitizedPathname);
    return `${resolved.origin}${sanitizedPath}`;
  } catch {
    const trimmed: string = sanitizeSlashes(sanitizedCandidate.trim() || '/');
    if (!isLikelyValidPath(trimmed)) {
      return '/';
    }
    const normalizedPath: string = ensureDirectoryPath(trimmed);
    return joinOriginAndPath(fallbackOrigin, normalizedPath);
  }
};

export const publicPathTestingApi = {
  sanitizeSlashes,
  ensureDirectoryPath,
  isLikelyValidPath,
  isLikelyFileSegment,
  joinOriginAndPath,
  computePublicPath,
};
